import { create } from 'zustand'
import type { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  initialized: boolean
  initialize: () => void
  signInWithPassword: (email: string, password: string) => Promise<{ error: Error | null }>
  signInWithGitHub: () => Promise<void>
  signInWithAzure: () => Promise<void>
  signOut: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: true,
  initialized: false,

  initialize: () => {
    if (get().initialized) return

    set({ initialized: true })

    // Single source of truth: onAuthStateChange handles ALL auth state updates
    // This includes INITIAL_SESSION on page load, SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      // Debug logging - remove after fixing the issue
      console.log('[Auth] Event:', event, 'User:', session?.user?.email ?? 'none')

      // Only update state for meaningful events
      // Skip TOKEN_REFRESHED as it doesn't change the user
      if (event === 'TOKEN_REFRESHED') {
        return
      }

      set({
        session,
        user: session?.user ?? null,
        loading: false,
      })
    })

    // Fallback: If no INITIAL_SESSION event fires within 100ms, check session manually
    // This handles edge cases where the listener might miss the initial event
    const timeoutId = setTimeout(async () => {
      if (get().loading) {
        const { data: { session } } = await supabase.auth.getSession()
        set({
          session,
          user: session?.user ?? null,
          loading: false,
        })
      }
    }, 100)

    // Store cleanup function (not currently used but available for future)
    // @ts-expect-error - storing for potential cleanup
    get()._cleanup = () => {
      clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  },

  signInWithPassword: async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) {
      console.error('Email login error:', error.message)
      return { error }
    }
    return { error: null }
  },

  signInWithGitHub: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: {
        redirectTo: window.location.origin,
      },
    })
    if (error) {
      console.error('Login error:', error.message)
    }
  },

  signInWithAzure: async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: window.location.origin,
        scopes: 'email profile openid',
      },
    })
    if (error) {
      console.error('Azure login error:', error.message)
    }
  },

  signOut: async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      console.error('Logout error:', error.message)
    }
  },
}))
